import React, { useContext, useState, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { useLocale } from "../../context/LocaleContext";
import { t, SUPPORTED_LOCALES, extractLocaleFromPath } from "../../i18n";
import { getLocalizedDisplay } from "../../utils/localizedDisplay";
import { FaBars, FaTimes, FaPhone, FaGlobe, FaChevronDown } from "react-icons/fa";
import "./Header.css";

/* Hover-intent hook: opens immediately, closes after a delay so user can cross the gap */
function useHoverIntent(closeDelay = 300) {
  const [open, setOpen] = useState(false);
  const timeout = useRef(null);
  const onEnter = useCallback(() => { clearTimeout(timeout.current); setOpen(true); }, []);
  const onLeave = useCallback(() => { timeout.current = setTimeout(() => setOpen(false), closeDelay); }, [closeDelay]);
  return { open, onEnter, onLeave };
}

// Use public/ paths so logos are never bundled (avoids 404 on missing src/assets)
const BRAND_LOGOS = {
  gso: "/assets/brand/gso/logo-color.webp",
  "apex-nc": "/assets/brand/apex-nc/logo-horizontal.webp",
  "apex-tn": "/assets/brand/apex-tn/logo-horizontal.webp",
};

export default function Header() {
  const { state, dispatch } = useContext(ApplicationContext);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, setLocale } = useLocale();
  const [mobileExpanded, setMobileExpanded] = useState({});
  const [langOpen, setLangOpen] = useState(false);

  /* Hover-intent for each desktop dropdown */
  const servicesHover = useHoverIntent(280);
  const locationsHover = useHoverIntent(280);
  const resourcesHover = useHoverIntent(280);
  const languageHover = useHoverIntent(280);

  const brand = state.brand;
  const brandKey = brand.brandKey || 'gso';
  const services = state.taxonomy?.dimensions?.find((d) => d.id === "service")?.values || [];
  const locations = state.taxonomy?.dimensions?.find((d) => d.id === "location")?.values || [];

  // Filter locations to current brand. If the brand declares physicalSiteSlugs
  // (TMM ground-truth physical sites), restrict the visible navigation surface
  // to that set — long-tail city routes remain reachable by direct URL.
  const physicalSiteSlugs = brand.physicalSiteSlugs;
  const brandLocations = (physicalSiteSlugs && physicalSiteSlugs.length
    ? locations.filter(l => physicalSiteSlugs.includes(l.slug))
        .sort((a, b) => physicalSiteSlugs.indexOf(a.slug) - physicalSiteSlugs.indexOf(b.slug))
    : locations.filter(l => brand.locations?.includes(l.slug) || l.brand === brand.brandKey));

  // Detect current location from URL for context-aware service links
  const currentLocationSlug = (() => {
    const { pathWithoutLocale } = extractLocaleFromPath(location.pathname);
    const segments = pathWithoutLocale.split('/').filter(Boolean);
    // Pattern: /:locationSlug/:serviceSlug (2 segments where first is a known location)
    if (segments.length >= 1) {
      const candidate = segments[0];
      if (brandLocations.some(l => l.slug === candidate)) {
        return candidate;
      }
    }
    return brandLocations[0]?.slug || 'atlanta';
  })();

  // Group services by category
  const serviceCategories = state.taxonomy?.serviceCategories || [];

  const switchLanguage = (newLocale) => {
    setLocale(newLocale);
    setLangOpen(false);
    // Navigate to the same page with the new locale prefix
    const { pathWithoutLocale } = extractLocaleFromPath(location.pathname);
    const newPrefix = newLocale === 'en' ? '' : `/${newLocale}`;
    const newPath = newPrefix + (pathWithoutLocale === '/' ? '/' : pathWithoutLocale);
    navigate(newPath);
  };

  const closeMobile = useCallback(() => {
    dispatch({ type: "SET_MOBILE_MENU", payload: false });
  }, [dispatch]);

  const toggleMobileSection = (section) => {
    setMobileExpanded((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const handleHamburgerClick = (e) => {
    e.stopPropagation();
    dispatch({ type: "TOGGLE_MOBILE_MENU" });
  };

  const prefix = locale === 'en' ? '' : `/${locale}`;

  // Active route detection for neumorphic persistent state
  const path = location.pathname;
  const isActive = (route) => {
    const full = `${prefix}${route}`;
    return path === full || path === `${full}/`;
  };
  const isActivePrefix = (routePrefix) => {
    const full = `${prefix}${routePrefix}`;
    return path.startsWith(full);
  };

  return (
    <>
      <header className="site-header">
        <div className="header-container">
          <Link to={`${prefix}/`} className="header-logo">
            <img
              src={BRAND_LOGOS[brand.brandKey] || BRAND_LOGOS['gso']}
              alt={`${brand.name} logo`}
              className="header-logo-img"
              onError={(e) => {
                e.target.style.display = 'none';
                const fallback = e.target.nextSibling;
                if (fallback) fallback.style.display = 'block';
              }}
            />
            <span className="header-logo-text" style={{ display: 'none' }}>{brand.name}</span>
          </Link>

          <nav className="header-nav-desktop">
            <div className="nav-dropdown" onMouseEnter={servicesHover.onEnter} onMouseLeave={servicesHover.onLeave}>
              <span className={`nav-link neu-link${isActivePrefix(`/${currentLocationSlug}/`) ? ' active' : ''}`}>{t("nav.services", locale)}</span>
              <div className={`nav-dropdown-menu mega-menu${servicesHover.open ? ' js-open' : ''}`}>
                <div className="mega-menu-grid">
                  {serviceCategories.length > 0 ? serviceCategories.map((cat) => (
                    <div key={cat.id} className="nav-dropdown-group">
                      <span className="nav-dropdown-label">{cat.icon} {cat.label}</span>
                      {services.filter((s) => s.category === cat.id).map((s) => (
                        <Link key={s.slug} to={`${prefix}/${currentLocationSlug}/${s.slug}`} className="nav-dropdown-item">
                          {getLocalizedDisplay(s, locale)}
                        </Link>
                      ))}
                    </div>
                  )) : (
                    <div className="nav-dropdown-group">
                      {services.map((s) => (
                        <Link key={s.slug} to={`${prefix}/${currentLocationSlug}/${s.slug}`} className="nav-dropdown-item">
                          {getLocalizedDisplay(s, locale)}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="nav-dropdown" onMouseEnter={locationsHover.onEnter} onMouseLeave={locationsHover.onLeave}>
              <span className={`nav-link neu-link${brandLocations.some(l => isActive(`/${l.slug}`)) ? ' active' : ''}`}>{t("nav.locations", locale)}</span>
              <div className={`nav-dropdown-menu locations-mega-menu${locationsHover.open ? ' js-open' : ''}`}>
                <div className="locations-nav-grid">
                  {brandLocations.map((l) => (
                    <Link key={l.slug} to={`${prefix}/${l.slug}`} className="nav-dropdown-item">
                      {l.city}, {l.state}
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            <Link to={`${prefix}/meet-our-team`} className={`nav-link neu-link${isActivePrefix('/meet-our-team') ? ' active' : ''}`}>{t("nav.team", locale)}</Link>
            <Link to={`${prefix}/injuries`} className={`nav-link neu-link${isActivePrefix('/injuries') ? ' active' : ''}`}>{t("nav.injuries", locale)}</Link>

            <div className="nav-dropdown" onMouseEnter={resourcesHover.onEnter} onMouseLeave={resourcesHover.onLeave}>
              <span className={`nav-link neu-link${isActivePrefix('/blog') || isActivePrefix('/faqs') || isActivePrefix('/core-exercises') || isActivePrefix('/media-center') || isActivePrefix('/resources') ? ' active' : ''}`}>{t("nav.resources", locale)}</span>
              <div className={`nav-dropdown-menu resources-dropdown-menu${resourcesHover.open ? ' js-open' : ''}`} style={{ minWidth: 200 }}>
                <Link to={`${prefix}/blog`} className="nav-dropdown-item">📝 Blog</Link>
                <Link to={`${prefix}/faqs`} className="nav-dropdown-item">❓ FAQs</Link>
                <Link to={`${prefix}/core-exercises`} className="nav-dropdown-item">🏋️ Core Exercises</Link>
                <Link to={`${prefix}/media-center`} className="nav-dropdown-item">🎬 Media Center</Link>
                <Link to={`${prefix}/resources/patient-referral`} className="nav-dropdown-item">⚖️ Patient Referral</Link>
                {(brandKey === 'apex-nc') && (
                  <Link to={`${prefix}/new-patient-forms`} className="nav-dropdown-item">📄 Patient Forms</Link>
                )}
              </div>
            </div>

            <Link to={`${prefix}/contact`} className={`nav-link neu-link${isActive('/contact') ? ' active' : ''}`}>{t("nav.contact", locale)}</Link>
          </nav>

          <div className="header-actions">
            {/* Language Switcher — hover-intent like all nav dropdowns */}
            <div className="nav-dropdown language-switcher" onMouseEnter={languageHover.onEnter} onMouseLeave={languageHover.onLeave} style={{ position: 'relative' }}>
              <button className="language-toggle">
                <FaGlobe /> {SUPPORTED_LOCALES.find(l => l.code === locale)?.label || 'English'}
              </button>
              <div className={`language-dropdown nav-dropdown-menu${languageHover.open ? ' js-open' : ''}`}>
                {SUPPORTED_LOCALES.map(l => (
                  <button key={l.code} className={`lang-option ${l.code === locale ? 'active' : ''}`} onClick={() => switchLanguage(l.code)}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <a href={`tel:${brand.phone}`} className="header-cta-phone">
              <FaPhone /> {brand.phoneExisting ? `New: ${brand.phone}` : brand.phone}
            </a>
            {brand.phoneExisting && (
              <a href={`tel:${brand.phoneExisting}`} className="header-cta-phone header-cta-phone-existing">
                Existing: {brand.phoneExisting}
              </a>
            )}
            <Link to={`${prefix}/request-appt`} className="header-cta-btn">
              {t("cta.schedule", locale)}
            </Link>
          </div>

          <button className={`hamburger-menu ${state.isMobileMenuOpen ? "active" : ""}`} onClick={handleHamburgerClick} aria-label="Toggle navigation menu">
            {state.isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>
      </header>

      <div className={`mobile-backdrop ${state.isMobileMenuOpen ? "open" : ""}`} onClick={closeMobile} />

      <nav className={`mobile-sidebar ${state.isMobileMenuOpen ? "open" : ""}`}>
        <div className="mobile-sidebar-header">
          <button className="mobile-close-btn" onClick={closeMobile} aria-label="Close menu"><FaTimes /></button>
          <h2>{brand.shortName || brand.name}</h2>
        </div>
        <div className="mobile-sidebar-links">
          <Link to={`${prefix}/`} onClick={closeMobile}>{t("nav.home", locale)}</Link>

          <button className={`mobile-accordion-toggle ${mobileExpanded.services ? "expanded" : ""}`} onClick={() => toggleMobileSection("services")}>
            {t("nav.services", locale)} <FaChevronDown className="mobile-chevron" />
          </button>
          {mobileExpanded.services && (
            <div className="mobile-accordion-content">
              {services.map((s) => (
                <Link key={s.slug} to={`${prefix}/${currentLocationSlug}/${s.slug}`} onClick={closeMobile}>{getLocalizedDisplay(s, locale)}</Link>
              ))}
            </div>
          )}

          <button className={`mobile-accordion-toggle ${mobileExpanded.locations ? "expanded" : ""}`} onClick={() => toggleMobileSection("locations")}>
            {t("nav.locations", locale)} <FaChevronDown className="mobile-chevron" />
          </button>
          {mobileExpanded.locations && (
            <div className="mobile-accordion-content">
              {brandLocations.map((l) => (
                <Link key={l.slug} to={`${prefix}/${l.slug}`} onClick={closeMobile}>{l.city}</Link>
              ))}
            </div>
          )}

          <Link to={`${prefix}/meet-our-team`} onClick={closeMobile}>{t("nav.team", locale)}</Link>
          <Link to={`${prefix}/injuries`} onClick={closeMobile}>{t("nav.injuries", locale)}</Link>

          <button className={`mobile-accordion-toggle ${mobileExpanded.resources ? "expanded" : ""}`} onClick={() => toggleMobileSection("resources")}>
            {t("nav.resources", locale)} <FaChevronDown className="mobile-chevron" />
          </button>
          {mobileExpanded.resources && (
            <div className="mobile-accordion-content">
              <Link to={`${prefix}/blog`} onClick={closeMobile}>📝 Blog</Link>
              <Link to={`${prefix}/faqs`} onClick={closeMobile}>❓ FAQs</Link>
              <Link to={`${prefix}/core-exercises`} onClick={closeMobile}>🏋️ Core Exercises</Link>
              <Link to={`${prefix}/media-center`} onClick={closeMobile}>🎬 Media Center</Link>
              <Link to={`${prefix}/resources/patient-referral`} onClick={closeMobile}>⚖️ Patient Referral</Link>
              {brandKey === 'apex-nc' && (
                <Link to={`${prefix}/new-patient-forms`} onClick={closeMobile}>📄 Patient Forms</Link>
              )}
            </div>
          )}
          <Link to={`${prefix}/contact`} onClick={closeMobile}>{t("nav.contact", locale)}</Link>

          <hr />
          <a href={`tel:${brand.phone}`} className="mobile-cta-phone"><FaPhone /> {brand.phoneExisting ? `New: ${brand.phone}` : brand.phone}</a>
          {brand.phoneExisting && (
            <a href={`tel:${brand.phoneExisting}`} className="mobile-cta-phone mobile-cta-phone-existing">Existing: {brand.phoneExisting}</a>
          )}
          <Link to={`${prefix}/request-appt`} className="mobile-cta-appt" onClick={closeMobile}>{t("cta.schedule", locale)}</Link>
          
          <div className="mobile-lang-list">
            {SUPPORTED_LOCALES.map(l => (
              <button key={l.code} className={`lang-option ${l.code === locale ? 'active' : ''}`} onClick={() => { switchLanguage(l.code); closeMobile(); }}>
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </nav>
    </>
  );
}
